import { useState, useMemo, useEffect, useRef } from "react";
import { Check, Sparkles, ChevronDown, ChevronUp, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTags } from "@/hooks/useTags";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

export type MediaContentType = 'food-item' | 'interior' | 'exterior' | 'menu' | 'general';
export type ServiceType = 'dine-in' | 'takeout' | 'delivery';

interface QuickTagSelectorProps {
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  contentType?: MediaContentType;
  cuisineHint?: string;
  aiSuggestedTags?: string[];
  className?: string;
  serviceType?: ServiceType;
  rating?: number;
  restaurantCuisine?: string | null;  // For auto-selecting cuisine tag
}

// Content type priorities
const CONTENT_TYPE_TAG_PRIORITY: Record<MediaContentType, string[]> = {
  'food-item': ['delicious', 'generous portions', 'well-presented', 'fresh', 'flavorful', 'authentic'],
  'interior': ['cozy', 'modern', 'rustic', 'romantic', 'lively', 'clean', 'spacious'],
  'exterior': ['outdoor seating', 'great location', 'easy parking', 'nice view', 'instagrammable'],
  'menu': ['good variety', 'clear pricing', 'seasonal specials', 'dietary options'],
  'general': ['would recommend', 'good value', 'friendly staff', 'quick service'],
};

// Service type specific tags (used for priority ordering only — not for inclusion/exclusion)
const SERVICE_TYPE_TAG_PRIORITY: Record<ServiceType, string[]> = {
  'dine-in': ['great atmosphere', 'good service', 'clean', 'cozy', 'romantic', 'lively', 'spacious', 'outdoor seating'],
  'takeout': ['quick pickup', 'well-packaged', 'still hot', 'accurate order', 'easy parking', 'convenient location'],
  'delivery': ['fast delivery', 'well-packaged', 'still hot', 'accurate order', 'good portions', 'great value'],
};

// Rating-based tags: low ratings suggest issue tags, high ratings suggest positive tags
const RATING_TAG_ADJUSTMENTS = {
  low: ['slow service', 'overpriced', 'small portions', 'inconsistent', 'noisy', 'crowded'],  // < 5
  medium: ['good value', 'decent portions', 'average'],  // 5-7
  high: ['excellent', 'must try', 'would recommend', 'authentic', 'best in class', 'hidden gem'],  // > 7
};

export default function QuickTagSelector({
  selectedTags,
  onTagsChange,
  contentType = 'general',
  cuisineHint,
  aiSuggestedTags = [],
  className,
  serviceType = 'dine-in',
  rating = 7.5,
  restaurantCuisine,
}: QuickTagSelectorProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const { tagCategories, isLoading, getCuisineTags } = useTags();

  // Only include tags from categories and tag entries that are valid for the current service type.
  // This is the DB-driven source of truth — no hardcoded name-based exclusion needed.
  const allTags = useMemo(() => {
    if (!tagCategories) return [];
    return tagCategories
      .filter(cat => cat.experienceTypes.includes(serviceType))
      .flatMap(cat => cat.tags.filter(t => t.orderTypes.includes(serviceType)).map(t => t.label));
  }, [tagCategories, serviceType]);
  
  // Track which cuisine has been auto-selected to prevent repeated additions
  const autoSelectedCuisineRef = useRef<string | null>(null);
  
  // Auto-select cuisine tag when restaurant is matched
  useEffect(() => {
    if (restaurantCuisine && tagCategories) {
      // Skip if we've already auto-selected for this cuisine
      if (autoSelectedCuisineRef.current === restaurantCuisine) return;
      
      const cuisineLower = restaurantCuisine.toLowerCase();
      const cuisineTag = allTags.find(tag => 
        tag.toLowerCase() === cuisineLower || 
        tag.toLowerCase().includes(cuisineLower) ||
        cuisineLower.includes(tag.toLowerCase())
      );
      if (cuisineTag && !selectedTags.includes(cuisineTag)) {
        autoSelectedCuisineRef.current = restaurantCuisine;
        onTagsChange([...selectedTags, cuisineTag]);
      }
    }
  }, [restaurantCuisine, tagCategories, allTags, selectedTags, onTagsChange]);

  const { priorityTags, otherTags } = useMemo(() => {
    // allTags is already filtered to the current serviceType — no further exclusion needed.
    const contentPriority = CONTENT_TYPE_TAG_PRIORITY[contentType] || [];
    const servicePriority = SERVICE_TYPE_TAG_PRIORITY[serviceType] || [];

    // Rating-based priority
    let ratingPriority: string[] = [];
    if (rating < 5) {
      ratingPriority = RATING_TAG_ADJUSTMENTS.low;
    } else if (rating <= 7) {
      ratingPriority = RATING_TAG_ADJUSTMENTS.medium;
    } else {
      ratingPriority = RATING_TAG_ADJUSTMENTS.high;
    }

    const aiTags = aiSuggestedTags.filter(t => !contentPriority.includes(t));

    const prioritySet = new Set([
      ...aiTags,
      ...contentPriority,
      ...servicePriority,
      ...ratingPriority,
    ]);

    const priority = allTags.filter(tag => {
      const normalizedTag = tag.toLowerCase();
      return Array.from(prioritySet).some(p => normalizedTag.includes(p.toLowerCase()));
    });

    if (priority.length < 8 && contentType === 'food-item') {
      const foodTags = allTags.filter(t =>
        t.toLowerCase().includes('flavor') ||
        t.toLowerCase().includes('portion') ||
        t.toLowerCase().includes('fresh') ||
        t.toLowerCase().includes('tasty')
      );
      foodTags.forEach(t => {
        if (!priority.includes(t) && priority.length < 8) {
          priority.push(t);
        }
      });
    }

    const other = allTags.filter(t => !priority.includes(t));
    return { priorityTags: priority.slice(0, 12), otherTags: other };
  }, [allTags, contentType, aiSuggestedTags, serviceType, rating]);

  // Get cuisine tags separately
  const cuisineTags = useMemo(() => {
    return getCuisineTags().map(t => t.label);
  }, [getCuisineTags]);
  
  // Non-cuisine categories for organized expansion, filtered to current service type.
  // Tags within each category are also filtered so only service-type-valid tags appear.
  // Categories with no valid tags after filtering are hidden entirely.
  const nonCuisineCategories = useMemo(() => {
    return tagCategories
      .filter(cat => cat.id !== 'cuisine' && cat.experienceTypes.includes(serviceType))
      .map(cat => ({
        ...cat,
        tags: cat.tags.filter(t => t.orderTypes.includes(serviceType)),
      }))
      .filter(cat => cat.tags.length > 0);
  }, [tagCategories, serviceType]);
  
  // Selected cuisine tags (for display near restaurant)
  const selectedCuisineTags = useMemo(() => {
    return selectedTags.filter(tag => cuisineTags.includes(tag));
  }, [selectedTags, cuisineTags]);
  
  // Non-cuisine priority tags (exclude cuisine from quick tags display)
  const nonCuisinePriorityTags = useMemo(() => {
    return priorityTags.filter(tag => !cuisineTags.includes(tag));
  }, [priorityTags, cuisineTags]);

  const toggleTag = (tag: string) => {
    if (selectedTags.includes(tag)) {
      onTagsChange(selectedTags.filter(t => t !== tag));
    } else {
      onTagsChange([...selectedTags, tag]);
    }
  };
  
  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const visibleTags = nonCuisinePriorityTags;

  if (isLoading) {
    return (
      <div className={cn("space-y-3", className)}>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Sparkles className="w-4 h-4" />
          <span>Loading tags...</span>
        </div>
      </div>
    );
  }

  // Helper to render a tag badge
  const renderTagBadge = (tag: string) => {
    const isSelected = selectedTags.includes(tag);
    
    return (
      <Badge
        key={tag}
        variant={isSelected ? "default" : "outline"}
        className={cn(
          "cursor-pointer transition-all text-sm py-1.5 px-3",
          isSelected && "bg-primary text-primary-foreground"
        )}
        onClick={() => toggleTag(tag)}
      >
        {isSelected && <Check className="w-3 h-3 mr-1" />}
        {tag}
      </Badge>
    );
  };

  return (
    <div className={cn("space-y-3", className)}>
      {/* Quick Tags Section */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          <Sparkles className="w-4 h-4 text-primary" />
          <span className="font-medium">Quick Tags</span>
          {aiSuggestedTags.length > 0 && (
            <Badge variant="secondary" className="text-xs">AI suggested</Badge>
          )}
        </div>
        <span className="text-xs text-muted-foreground">
          {selectedTags.length} selected
        </span>
      </div>

      {/* Priority Tags */}
      <div className="flex flex-wrap gap-2">
        {visibleTags.map((tag) => renderTagBadge(tag))}
      </div>

      {/* Show More Button - expands into category view */}
      {nonCuisineCategories.length > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full text-muted-foreground hover:text-foreground"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="w-4 h-4 mr-1" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4 mr-1" />
              Browse all tags by category
            </>
          )}
        </Button>
      )}
      
      {/* Expanded Category View */}
      {isExpanded && (
        <div className="space-y-2 border-t pt-3">
          {/* Tag Categories (cuisine handled in restaurant header) */}
          {nonCuisineCategories.map(category => {
            const isOpen = expandedCategories.has(category.id);
            const categorySelectedCount = category.tags.filter(
              t => selectedTags.includes(t.label)
            ).length;
            
            return (
              <Collapsible 
                key={category.id} 
                open={isOpen} 
                onOpenChange={() => toggleCategory(category.id)}
              >
                <CollapsibleTrigger className="flex items-center justify-between w-full p-2 rounded-md hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-2">
                    <ChevronRight className={cn(
                      "w-4 h-4 transition-transform",
                      isOpen && "rotate-90"
                    )} />
                    <span className="font-medium text-sm">{category.label}</span>
                  </div>
                  {categorySelectedCount > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {categorySelectedCount}
                    </Badge>
                  )}
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="flex flex-wrap gap-2 pl-6 pt-2 pb-2">
                    {category.tags.map(t => renderTagBadge(t.label))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            );
          })}
        </div>
      )}
    </div>
  );
}
